import { useCallback, useContext, useMemo, useRef } from 'react'
import { toPath } from '../internal/formStore'
import { FieldPrefixContext, useFieldState } from './context'

/**
 * antd Form.List. Calls `children(fields, { add, remove, move }, { errors })`,
 * where each field carries `{ key, name, }` and `name` is the index inside the list.
 *
 * `key` stays stable across reorders and removals, which is what keeps inputs
 * from swapping their DOM nodes (the classic Form.List bug).
 */
const FormList = ({ name, initialValue, rules, children }) => {
  const { context } = useFieldState(name, true)
  const prefix = useContext(FieldPrefixContext)
  const form = context?.form
  const fullName = useMemo(() => [...prefix, ...toPath(name)], [prefix, name])

  const keysRef = useRef([])
  const nextKeyRef = useRef(0)

  const rawValue = form?.getFieldValue(fullName)
  const items = Array.isArray(rawValue) ? rawValue : []

  // Keep exactly one stable key per row.
  while (keysRef.current.length < items.length) {
    keysRef.current.push(nextKeyRef.current)
    nextKeyRef.current += 1
  }
  if (keysRef.current.length > items.length) {
    keysRef.current = keysRef.current.slice(0, items.length)
  }

  const setList = useCallback(
    (next, nextKeys) => {
      keysRef.current = nextKeys
      form?.setFieldValue(fullName, next)
    },
    [form, fullName]
  )

  const add = useCallback(
    (defaultValue, insertIndex) => {
      const next = [...items]
      const keys = [...keysRef.current]
      const key = nextKeyRef.current
      nextKeyRef.current += 1

      if (insertIndex == null || insertIndex >= next.length) {
        next.push(defaultValue)
        keys.push(key)
      } else {
        next.splice(insertIndex, 0, defaultValue)
        keys.splice(insertIndex, 0, key)
      }
      setList(next, keys)
    },
    [items, setList]
  )

  const remove = useCallback(
    (index) => {
      const targets = Array.isArray(index) ? index : [index]
      const next = items.filter((_, position) => !targets.includes(position))
      const keys = keysRef.current.filter((_, position) => !targets.includes(position))
      setList(next, keys)
    },
    [items, setList]
  )

  const move = useCallback(
    (from, to) => {
      if (from === to || from == null || to == null) return
      const next = [...items]
      const keys = [...keysRef.current]
      const [movedValue] = next.splice(from, 1)
      const [movedKey] = keys.splice(from, 1)
      next.splice(to, 0, movedValue)
      keys.splice(to, 0, movedKey)
      setList(next, keys)
    },
    [items, setList]
  )

  const fields = items.map((_, index) => ({
    key: keysRef.current[index],
    name: index,
    isListField: true,
    fieldKey: keysRef.current[index],
  }))

  const errors = form?.getFieldError(fullName) ?? []

  return (
    <FieldPrefixContext.Provider value={fullName}>
      {children(fields, { add, remove, move }, { errors })}
    </FieldPrefixContext.Provider>
  )
}

export default FormList
